import { makeObservable, observable, computed, action, runInAction } from 'mobx';
import swal from 'sweetalert2';

import Participant from './Participant';

class Day {
    date;

    constructor(day) {
        makeObservable(this, {
            date: observable,
            is_today: computed,
            to_s: computed,
            for_header: computed
        });

        const [year, month, actday] = day.split('-');
        this.date = new Date(year, month - 1, actday);
    }

    get is_today() {
        const today = new Date();
        return today.getFullYear() === this.date.getFullYear() &&
            today.getMonth() === this.date.getMonth() &&
            today.getDate() === this.date.getDate();
    }

    get to_s() {
        return `${this.date.getFullYear()}-${('00' + (this.date.getMonth() + 1)).slice(-2)}-${('00' + this.date.getDate()).slice(-2)}`;
    }

    get for_header() {
        return `${this.date.getMonth() + 1}/${this.date.getDate()}`;
    }
}

export default class Event {
    parent;
    guid;
    title;
    presenters;
    knet_id;
    days = [];
    participant_list = [];
    show_deleted = false;

    constructor(guid, parent) {
        makeObservable(this, {
            parent: observable,
            guid: observable,
            title: observable,
            presenters: observable,
            knet_id: observable,
            days: observable,
            participant_list: observable,
            show_deleted: observable,
            total_unique_registrations: computed,
            non_deleted_participants: computed,
            has_deleted_participants: computed,
            can_register: computed,
            days_description: computed,
            participants: computed,
            participants_sorted: computed,
            participants_filtered: computed,
            participants_by_group: computed,
            participant_groups: computed,
            participantsByUuid: computed,
            counts_by_day: computed,
            fillData: action.bound,
            checkInAll: action.bound
        });

        this.csrf = parent.csrf;
        this.guid = guid;
        this.parent = parent;
    }

    get can_register() {
        return !!this.days.filter(d => d.is_today).length
    }

    get days_description() {
        if (!this.days.length) {
            return '';
        }

        const firstDay = this.days[0];
        const lastDay = this.days[this.days.length - 1];

        if (firstDay.to_s == lastDay.to_s) {
            return firstDay.for_header;
        }

        return firstDay.for_header + ' - ' + lastDay.for_header;
    }

    get total_unique_registrations() {
        return this.participant_list.filter(p => {
            return p.checkins && p.checkins.length;
        }).length;
    }

    get non_deleted_participants() {
        return this.participant_list.filter(p => {
            return p.status && p.status.toLowerCase() === 'active';
        });
    }

    get has_deleted_participants() {
        return this.non_deleted_participants.length !== this.participant_list.length;
    }

    get participants() {
        if (this.show_deleted) {
            return this.participant_list;
        }

        return this.non_deleted_participants;
    }

    get participants_sorted() {
        return this.participants.slice().sort((a, b) => {
            return a.sort_name < b.sort_name ? -1 : 1;
        });
    }

    get participants_filtered() {
        if (!this.parent.filter) {
            return this.participants_sorted;
        }

        return this.participants_sorted.filter(p => {
            const filters = this.parent.filter.toLowerCase().split(' ');

            for (let i = 0; i < filters.length; i++) {
                const filter = filters[i];
                if (p.searchable_name.indexOf(filter) < 0) {
                    return false;
                }
            }

            return true;
        });
    }

    get participants_by_group() {
        const groups = {};

        this.participants_filtered.forEach(p => {
            const group = p[this.parent.participant_group_by];

            if (groups[group] === undefined) {
                groups[group] = [];
            }

            groups[group].push(p);
        });

        return groups;
    }

    get participant_groups() {
        return Object.keys(this.participants_by_group).sort();
    }

    get participantsByUuid() {
        const uuids = {};

        for (const participant of this.participant_list) {
            uuids[participant.uuid] = participant;
        }

        return uuids;
    }

    get counts_by_day() {
        const counts = {};

        counts['total'] = this.participant_list.length;

        for (const day of this.days) {
            if (day) {
               counts[day.to_s] = this.participant_list.filter(p => p.checkins.find(c => c === day.to_s));
            }
        }

        return counts;
    }

    get counts_by_day_by_group() {
        const counts = {};
        const groups = this.participant_groups;
        const participants = this.participants_by_group;

        for (const group of groups) {
            counts[group] = {
                total: participants[group].length
            }

            for (const day of this.days) {
                if (day) {
                    counts[group][day.to_s] = participants[group].filter(p => p.checkins.find(c => c === day.to_s));
                }
            }
        }

        return counts;
    }

    checkInAll(day, group) {
        swal({
            title: 'Are You Sure?',
            text: `Are you sure you want to check everyone in for ${day.for_header}?`,
            showCancelButton: true,
        }).then(({ dismiss }) => {
            if (dismiss !== 'cancel') {
                const parts = group ? this.participants_by_group[group] : this.participants;

                for (const participant of parts) {
                    const checkedIn = participant.checkins.indexOf(day.to_s) >= 0;

                    if (!checkedIn) {
                        participant.check_in(day);
                    }
                }
            }
        });
    }

    fillData(data) {
        runInAction(() => {
            if (!this.days.length && data.days) {
                this.days = observable(data.days.map(d => new Day(d)));
            }

            if (!this.knet_id && data.knet_id) {
                this.knet_id = data.knet_id;
            }

            if (!this.title && data.title) {
                this.title = data.title;
            }

            if (!this.presenters && data.presenters) {
                this.presenters = data.presenters;
            }

            const participants = data.registrations;
            const missingParticipants = this.participant_list.map(p => p.uuid);

            for (const participant of participants) {
                let p = this.participantsByUuid[participant.uuid];

                if (!p) {
                    p = new Participant(participant, this);
                    this.participant_list.push(p);
                } else {
                    if (!p.is_editing) {
                        p.update(participant);
                    }
                }

                missingParticipants.splice(missingParticipants.indexOf(participant.uuid), 1);
            }

            for (const uuid of missingParticipants) {
                const p = this.participantsByUuid[uuid];
                const i = this.participant_list.indexOf(p);
                this.participant_list = this.participant_list.slice(0, i).concat(this.participant_list.slice(i + 1));
            }
        });
    }
}